import {
    Dispatch,
    FC,
    KeyboardEventHandler,
    memo,
    SetStateAction,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { Box, SxProps, Theme, useTheme } from '@mui/material';
import {
    Booking,
    BookingCalendarSelection,
    BookingDeviceModalData,
    BookingDeviceModalMode,
    BookingStatus,
    Device,
    SelectedBookingId,
    SlotIndex,
} from '../types';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '@features/auth/store/selectors';
import { selectAllBookings } from '../store/selectors';
import isEqual from 'lodash/isEqual';
import { UserProfile } from '@features/users';
import {
    calculateActiveBookingStyles,
    getBookingForItem,
    getBookingSelectionFromDaysAndHours,
    isActiveBooking,
    isPastCalendarItem,
    useIsBookingSelected,
} from '@pages/booking/utils';
import { BookingState } from '@pages/booking/store';
import { getDatesDifferenceFromDateRange } from '@shared/utils/utils';
import { useBookingConfigContext } from '@pages/booking/contexts/BookingConfigContext';
import { AccessEnum } from '@shared/types';

export function getCalendarItemStyle(theme: Theme) {
    return {
        padding: 1,
        flex: 1,
        position: 'relative',
        outline: 'none',
        backgroundColor: theme.palette.background.paper,
        border: '1px solid var(--border-color)',
        borderTop: 'none',
        borderRight: 'none',
        maxWidth: '100%',
    };
}

function getHoverStyle(theme: Theme) {
    return {
        boxShadow:
            theme.palette.mode === 'light'
                ? 'inset 0 0 0 999px rgba(0, 0, 0, 0.06)'
                : 'inset 0 0 0 999px rgba(255, 255, 255, 0.2)',
    };
}

function getMyBookingBackground(bookingForItem: Booking, isInPast: boolean, theme: Theme) {
    if (bookingForItem.status === BookingStatus.ERROR) {
        return 'var(--booking-error-bg-color)';
    }
    if (isInPast) {
        return theme.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255, 255, 255, 0.16)';
    }
    return 'var(--primary-focus-visible)';
}

function getOthersBookingBackground(theme: Theme, isInPast: boolean) {
    if (isInPast) {
        return theme.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255, 255, 255, 0.16)';
    }
    return 'var(--primary-hover)';
}

function getMyActiveBookingBorderColor(theme: Theme, bookingForItem: Booking) {
    return bookingForItem?.status === BookingStatus.ERROR
        ? 'var(--booking-error-border-color)'
        : theme.palette.primary.main;
}

interface BookingCalendarItemProps {
    dateTime: Date;
    isInSelection: boolean;
    setBookingsSelection: Dispatch<SetStateAction<BookingCalendarSelection>>;
    setModalData: Dispatch<SetStateAction<BookingDeviceModalData | null>>;
    selectedBookingId: SelectedBookingId;
    setSelectedBookingId: (newBookingId: SelectedBookingId) => void;
    tabIndex?: number;
    selectedDevice?: Device;
    slotIndex?: SlotIndex;
    slotsLength?: number;
    isFirstDayAndFirstHour?: boolean;
}

const BookingCalendarItem: FC<BookingCalendarItemProps> = ({
    dateTime,
    isInSelection,
    setBookingsSelection,
    setModalData,
    selectedBookingId,
    setSelectedBookingId,
    tabIndex,
    selectedDevice,
    slotIndex,
    slotsLength,
    isFirstDayAndFirstHour,
}) => {
    const theme = useTheme();
    const { localBookingsStorageMode } = useBookingConfigContext();
    const bookings = useSelector(
        (state: { booking: BookingState }) => selectAllBookings(state, selectedDevice?.pk),
        isEqual,
    );
    const bookingForItem: Booking | null = useMemo(
        () => getBookingForItem(dateTime, bookings, slotIndex),
        [bookings, dateTime, slotIndex],
    );
    const isBookingSelected = useIsBookingSelected({ bookingForItem, selectedDevice });
    const currentUser = useSelector(selectCurrentUser);
    const isInBooking = Boolean(bookingForItem && bookingForItem.pk && bookingForItem.booked_from);
    const isInMyBooking = Boolean(bookingForItem && bookingForItem.created_by_user === currentUser);
    const isFirstItemOfBooking =
        bookingForItem && new Date(bookingForItem.booked_from).getTime() === dateTime.getTime();
    const isLastItemOfBooking =
        bookingForItem &&
        new Date(dateTime).getTime() === new Date(bookingForItem.booked_to).getTime() - 60 * 60 * 1000;
    const isItemWithSlots = slotIndex !== undefined && slotsLength !== undefined;
    const isFirstSlotOfBooking =
        bookingForItem && isItemWithSlots ? (bookingForItem.slots as number[])[0] === slotIndex : true;
    const isLastSlotOfBooking =
        bookingForItem && isItemWithSlots ? (bookingForItem.slots as number[]).at(-1) === slotIndex : true;
    const isFirstItemInHour = Boolean(slotIndex === undefined || slotIndex === 1);
    const isInPast = isPastCalendarItem(dateTime, false);
    const isDisabledInPast = !isInBooking && isInPast;
    const clickDisabled = !selectedDevice || isDisabledInPast;
    const isInMyActiveBooking = isInMyBooking && isActiveBooking(bookingForItem);

    const handleInitializeBookingDeviceModal = useCallback(() => {
        function getBookingViewMode(): BookingDeviceModalMode | undefined {
            let viewMode;

            if (!isInBooking) {
                viewMode = BookingDeviceModalMode.CREATE;
            } else if (isInMyBooking && bookingForItem) {
                if (isInPast && !isInMyActiveBooking) {
                    viewMode = BookingDeviceModalMode.VIEW;
                } else if (bookingForItem.readonly) {
                    viewMode = BookingDeviceModalMode.VIEW;
                } else {
                    viewMode = BookingDeviceModalMode.MANAGE;
                }
            } else if (bookingForItem && bookingForItem.created_by_user) {
                if (bookingForItem.access === AccessEnum.Public) {
                    viewMode = BookingDeviceModalMode.MANAGE;
                } else {
                    viewMode = BookingDeviceModalMode.VIEW;
                }
            }

            return viewMode;
        }

        function getBookingSelection(viewMode?: BookingDeviceModalMode): BookingCalendarSelection {
            if (!viewMode || !bookingForItem) {
                return {};
            }

            if (viewMode === BookingDeviceModalMode.CREATE) {
                return {
                    [`${dateTime.toISOString()}`.concat(slotIndex !== undefined ? `_${slotIndex}` : '')]: true,
                };
            } else {
                // add all items of booking to selection
                const [days, hours] = getDatesDifferenceFromDateRange(
                    bookingForItem.booked_from,
                    bookingForItem.booked_to,
                );

                return getBookingSelectionFromDaysAndHours(
                    days,
                    hours,
                    new Date((bookingForItem as Booking).booked_from),
                    slotIndex ? bookingForItem?.slots : undefined,
                );
            }
        }

        const viewMode = getBookingViewMode();
        const bookingSelection = getBookingSelection(viewMode);

        if (viewMode !== undefined) {
            let slotToSelect;

            if (slotIndex !== undefined) {
                // if MANAGE mode we should force select first slot of booking (no matter which slot was clicked)
                slotToSelect = viewMode === BookingDeviceModalMode.MANAGE ? bookingForItem?.slots?.[0] : slotIndex;
            }

            setBookingsSelection(bookingSelection);
            setModalData({
                mode: viewMode,
                dateTime: dateTime,
                bookingForItem: bookingForItem,
                slotIndex: slotToSelect,
                open: true,
            });
        }
    }, [
        bookingForItem,
        dateTime,
        isInBooking,
        isInMyActiveBooking,
        isInMyBooking,
        isInPast,
        setBookingsSelection,
        setModalData,
        slotIndex,
    ]);

    // Open booking if it is in url params
    useEffect(() => {
        if (!bookingForItem || localBookingsStorageMode) {
            return;
        }

        if (selectedBookingId === bookingForItem.pk) {
            handleInitializeBookingDeviceModal();
        }
    }, [bookingForItem, handleInitializeBookingDeviceModal, localBookingsStorageMode, selectedBookingId]);

    const handleCalendarItemInteraction = () => {
        bookingForItem && setSelectedBookingId(bookingForItem.pk);
        handleInitializeBookingDeviceModal();
    };

    const handleCalendarItemClick = (e: SyntheticEvent) => {
        e && e.stopPropagation();
        handleCalendarItemInteraction();
    };

    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        e && e.stopPropagation();

        if (e.key === 'Enter') {
            handleCalendarItemInteraction();
        }
    };

    const slotsStyles: { rootStyle: SxProps; childStyle?: SxProps } | undefined = useMemo(() => {
        if (!bookingForItem || !isInMyActiveBooking || !isItemWithSlots || bookingForItem.isLocal) {
            return;
        }

        return calculateActiveBookingStyles(
            getMyActiveBookingBorderColor(theme, bookingForItem),
            Boolean(isFirstItemOfBooking),
            Boolean(isLastItemOfBooking),
            Boolean(isFirstSlotOfBooking),
            Boolean(isLastSlotOfBooking),
        );
    }, [
        bookingForItem,
        isFirstItemOfBooking,
        isFirstSlotOfBooking,
        isInMyActiveBooking,
        isItemWithSlots,
        isLastItemOfBooking,
        isLastSlotOfBooking,
        theme,
    ]);

    return (
        <Box
            tabIndex={tabIndex}
            sx={{
                ...getCalendarItemStyle(theme),
                overflow: isItemWithSlots ? undefined : 'hidden',
                cursor: clickDisabled ? 'initial' : 'pointer',
                ':hover': clickDisabled ? {} : getHoverStyle(theme),
                ':focus-visible': {
                    outline: 'none',
                    ...getHoverStyle(theme),
                },
                backgroundColor:
                    isInBooking && bookingForItem
                        ? isInMyBooking
                            ? getMyBookingBackground(bookingForItem, isInPast, theme)
                            : getOthersBookingBackground(theme, isInPast)
                        : theme.palette.background.paper,
                border: isInBooking ? '1px solid transparent' : '1px solid var(--border-color)',
                ...((isInSelection || isBookingSelected) && {
                    backgroundImage:
                        theme.palette.mode === 'light'
                            ? `repeating-linear-gradient(135deg, rgba(0,0,0,${isBookingSelected ? '0.2' : '0.14'}) 0 10px, transparent 10px 20px)`
                            : 'repeating-linear-gradient(135deg, rgba(255,255,255,0.24) 0 10px, transparent 10px 20px)',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'repeat',
                    backgroundPosition: '0 0',
                }),
                ...(!isInBooking &&
                    isDisabledInPast && {
                        boxShadow:
                            theme.palette.mode === 'light'
                                ? 'inset 0 0 0 999px rgba(0, 0, 0, 0.08)'
                                : 'inset 0 0 0 999px rgba(255, 255, 255, 0.09)',
                    }),
                ...(isInBooking && (isFirstItemInHour || isFirstSlotOfBooking)
                    ? {
                          borderLeft: '1px solid var(--border-color)',
                      }
                    : {}),
                ...(isInBooking && isLastItemOfBooking
                    ? {
                          borderBottom: '1px solid var(--border-color)',
                      }
                    : {}),
                ...(slotsStyles !== undefined ? slotsStyles.rootStyle : {}),
                ...(isInMyActiveBooking &&
                    bookingForItem &&
                    !isItemWithSlots &&
                    !bookingForItem.isLocal &&
                    !localBookingsStorageMode && {
                        // for device without slots
                        borderLeft: isFirstSlotOfBooking
                            ? `4px solid ${getMyActiveBookingBorderColor(theme, bookingForItem)}`
                            : 'none',
                        borderTop: isFirstItemOfBooking
                            ? `4px solid ${getMyActiveBookingBorderColor(theme, bookingForItem)}`
                            : 'none',
                        borderRight: isLastSlotOfBooking
                            ? `4px solid ${getMyActiveBookingBorderColor(theme, bookingForItem)}`
                            : 'none',
                        borderBottom: isLastItemOfBooking
                            ? `4px solid ${getMyActiveBookingBorderColor(theme, bookingForItem)}`
                            : 'none',
                        p: 0.5,
                    }),
            }}
            onClick={clickDisabled ? undefined : handleCalendarItemClick}
            onKeyDown={handleKeyDown}
        >
            {/* hack to use 4 pseudo elements for one calendar item*/}
            {slotsStyles?.childStyle && <Box sx={slotsStyles.childStyle} />}
            {!isItemWithSlots &&
                (isFirstItemOfBooking || isFirstDayAndFirstHour) &&
                bookingForItem?.created_by_user && (
                    <UserProfile
                        popoverInfo={{
                            avatar: true,
                            email: true,
                            profileLink: true,
                            rocket: true,
                            role: true,
                        }}
                        popoverTrigger="hover"
                        createdUserId={bookingForItem.created_by_user}
                        userContainerProps={{
                            sx: {
                                maxWidth: '100%',
                                color: isInMyBooking && bookingForItem.status === BookingStatus.ERROR ? 'white' : '',
                            },
                        }}
                    />
                )}
        </Box>
    );
};

export default memo(BookingCalendarItem, isEqual);
